import React, { useState, useMemo, useRef } from 'react';
import { 
  MapPin, 
  Clock, 
  ExternalLink, 
  Copy, 
  Check, 
  Search, 
  ChevronDown, 
  ChevronUp, 
  Navigation, 
  ShoppingBag, 
  AlertCircle,
  Compass,
  Building2
} from 'lucide-react';
import { publicLocations } from '../data/locations.js';
import { PublicLocation } from '../data/types.js';

interface LocationsInteractiveDirectoryProps {
  isEn?: boolean;
}

interface MapCoordinate {
  x: number;
  y: number;
  zone: 'reforma' | 'condesa' | 'napoles' | 'sur' | 'santa-fe';
}

const coordinatesBySlug: Record<string, MapCoordinate> = {
  'azueta': { x: 57.5, y: 44.0, zone: 'condesa' },      // Centro Histórico
  'bajio': { x: 42.5, y: 54.0, zone: 'condesa' },       // Hipódromo Condesa
  'dakota': { x: 45.0, y: 62.0, zone: 'napoles' },      // Nápoles
  'lerma': { x: 47.0, y: 43.0, zone: 'reforma' },       // Cuauhtémoc / Reforma
  'melchor': { x: 45.0, y: 40.0, zone: 'reforma' },     // Cuauhtémoc / Reforma
  'nazas': { x: 49.0, y: 41.0, zone: 'reforma' },       // Cuauhtémoc / Reforma
  'panuco': { x: 50.0, y: 39.0, zone: 'reforma' },      // Cuauhtémoc / Reforma
  'parroquia': { x: 49.0, y: 72.0, zone: 'napoles' },   // Del Valle
  'veracruz': { x: 40.0, y: 49.0, zone: 'condesa' },    // Condesa (Parque España)
  'yosemite': { x: 47.0, y: 63.5, zone: 'napoles' },    // Nápoles
  'benjamin-hill': { x: 39.0, y: 53.0, zone: 'condesa' }, // Hipódromo Condesa
  'ajusco': { x: 54.0, y: 80.0, zone: 'sur' },          // Portales Sur
  'santa-fe': { x: 18.0, y: 70.0, zone: 'santa-fe' },   // Santa Fe / Poniente
};

export const LocationsInteractiveDirectory: React.FC<LocationsInteractiveDirectoryProps> = ({ isEn = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedZone, setSelectedZone] = useState<string>('all');
  const [activeLocationId, setActiveLocationId] = useState<string>('loc-lerma');
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const listContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const zones = useMemo(() => [
    { id: 'all', label: isEn ? 'Todas (13)' : 'Todas (13)' },
    { id: 'reforma', label: isEn ? 'Reforma y Centro (5)' : 'Reforma y Centro (5)' },
    { id: 'condesa', label: isEn ? 'Condesa e Hipódromo (4)' : 'Condesa e Hipódromo (4)' },
    { id: 'napoles', label: isEn ? 'Nápoles y Del Valle (3)' : 'Nápoles y Del Valle (3)' },
    { id: 'sur', label: isEn ? 'Sur y Santa Fe (2)' : 'Sur y Santa Fe (2)' },
  ], [isEn]);

  const filteredLocations = useMemo(() => {
    return publicLocations.filter((loc) => {
      const coord = coordinatesBySlug[loc.slug];
      // Zone match
      if (selectedZone !== 'all') {
        if (selectedZone === 'reforma') {
          if (coord?.zone !== 'reforma' && loc.slug !== 'azueta') return false;
        } else if (selectedZone === 'condesa') {
          if (coord?.zone !== 'condesa' || loc.slug === 'azueta') return false;
        } else if (selectedZone === 'napoles') {
          if (coord?.zone !== 'napoles') return false;
        } else if (selectedZone === 'sur') {
          if (coord?.zone !== 'sur' && coord?.zone !== 'santa-fe') return false;
        }
      }

      // Search match
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const matchName = loc.name.toLowerCase().includes(q);
      const matchNeigh = (loc.neighborhood || '').toLowerCase().includes(q);
      const matchBorough = (loc.borough || '').toLowerCase().includes(q);
      const matchAddr = (loc.addressLine1 || '').toLowerCase().includes(q);
      return matchName || matchNeigh || matchBorough || matchAddr;
    });
  }, [searchQuery, selectedZone]);

  const handleSelectLocation = (id: string, smoothScroll = true) => {
    setActiveLocationId(id);
    if (smoothScroll && itemRefs.current[id] && listContainerRef.current) {
      const container = listContainerRef.current;
      const element = itemRefs.current[id];
      if (element) {
        const elemTop = element.offsetTop - container.offsetTop;
        container.scrollTo({ top: elemTop - 16, behavior: 'smooth' });
      }
    }
  };

  const handleCopyAddress = (loc: PublicLocation, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!loc.addressLine1) return;
    const full = `${loc.addressLine1}, Col. ${loc.neighborhood || ''}, ${loc.borough || ''}, C.P. ${loc.postalCode || ''}, CDMX`;
    navigator.clipboard.writeText(full).then(() => {
      setCopiedId(loc.id);
      setTimeout(() => setCopiedId(null), 2500);
    });
  };

  const activeLoc = useMemo(() => {
    return publicLocations.find(l => l.id === activeLocationId) || publicLocations[0];
  }, [activeLocationId]);

  return (
    <div className="space-y-6">
      {/* Search & Zone Filters Header */}
      <div className="bg-white rounded-2xl border border-[#E5E0D8] p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isEn ? 'Buscar por sucursal, colonia o alcaldía (ej. Cuauhtémoc, Nápoles, Bajío)...' : 'Buscar por sucursal, colonia o alcaldía (ej. Cuauhtémoc, Nápoles, Bajío)...'}
              className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-300 text-sm bg-[#FAF8F5] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-full w-5 h-5 flex items-center justify-center"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex items-center justify-between md:justify-end gap-3 text-xs text-gray-500">
            <span className="font-bold text-[#001557] whitespace-nowrap">
              {isEn ? `Mostrando ${filteredLocations.length} sucursales` : `Mostrando ${filteredLocations.length} sucursales`}
            </span>
          </div>
        </div>

        {/* Filter Chips */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-gray-500 mr-1">
            Zona:
          </span>
          {zones.map((z) => (
            <button
              key={z.id}
              onClick={() => setSelectedZone(z.id)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                selectedZone === z.id
                  ? 'bg-[#001557] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {z.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Split: Interactive Directory (Left) + CDMX Visual Map (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* LEFT COLUMN: Compact Interactive List (lg:col-span-6) */}
        <div className="lg:col-span-6 flex flex-col">
          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm overflow-hidden flex flex-col">
            <div className="p-4 sm:p-5 bg-gradient-to-r from-[#FAF8F5] to-white border-b border-[#E5E0D8] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#001557]" />
                <span className="font-heading font-black text-sm text-[#001557] uppercase tracking-wider">
                  Directorio Doña Blanca
                </span>
              </div>
              <span className="text-xs text-gray-500 font-medium">
                Selecciona para desplegar y ubicar
              </span>
            </div>

            {/* Scrollable list with fixed height */}
            <div 
              ref={listContainerRef}
              className="max-h-[640px] overflow-y-auto divide-y divide-gray-100 p-3 sm:p-4 space-y-2.5 custom-scrollbar"
            >
              {filteredLocations.length > 0 ? (
                filteredLocations.map((loc) => {
                  const isActive = activeLocationId === loc.id;
                  const isHovered = hoveredLocationId === loc.id;
                  
                  return (
                    <div
                      key={loc.id}
                      ref={(el) => (itemRefs.current[loc.id] = el)}
                      onClick={() => handleSelectLocation(loc.id, false)}
                      onMouseEnter={() => setHoveredLocationId(loc.id)}
                      onMouseLeave={() => setHoveredLocationId(null)}
                      className={`rounded-xl border transition-all duration-200 cursor-pointer p-4 ${
                        isActive
                          ? 'border-[#001557] bg-blue-50/40 shadow-sm ring-1 ring-[#001557]/20'
                          : isHovered
                          ? 'border-gray-300 bg-gray-50/80 shadow-xs'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      {/* Collapsed Header Summary */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                            isActive
                              ? 'bg-[#001557] text-white shadow-xs'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            <MapPin className="w-4 h-4" />
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className={`font-heading font-black text-base tracking-tight ${
                                isActive ? 'text-[#001557]' : 'text-gray-900'
                              }`}>
                                {loc.name}
                              </h3>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700">
                                {loc.neighborhood || loc.city}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              {loc.neighborhood ? `Col. ${loc.neighborhood} · ${loc.borough || 'CDMX'}` : loc.city}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                            Abierto hoy
                          </span>
                          <div className="text-gray-400 p-1">
                            {isActive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details Body */}
                      {isActive && (
                        <div className="mt-4 pt-3.5 border-t border-gray-200/80 space-y-3">
                              {/* Address with Copy Action */}
                              <div className="bg-white rounded-lg p-3 border border-gray-200/70 flex items-start justify-between gap-3">
                                <div className="text-xs text-gray-700 space-y-0.5">
                                  <p className="font-bold text-gray-900">{loc.addressLine1}</p>
                                  <p className="text-gray-500">
                                    {loc.neighborhood ? `Col. ${loc.neighborhood}, ` : ''}
                                    {loc.borough ? `${loc.borough}, ` : ''}
                                    {loc.postalCode ? `C.P. ${loc.postalCode}, ` : ''}
                                    {loc.city}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => handleCopyAddress(loc, e)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors shrink-0"
                                  title="Copiar dirección completa"
                                >
                                  {copiedId === loc.id ? (
                                    <>
                                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                                      <span className="text-emerald-700">¡Copiada!</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-3.5 h-3.5" />
                                      <span>Copiar</span>
                                    </>
                                  )}
                                </button>
                              </div>

                              {/* Schedules */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                {loc.breakfastHours && (
                                  <div className="bg-amber-50/60 border border-amber-100 rounded-lg p-2.5">
                                    <div className="flex items-center gap-1.5 font-bold text-amber-900">
                                      <Clock className="w-3.5 h-3.5 text-amber-700" />
                                      <span>Desayunos</span>
                                    </div>
                                    <p className="text-amber-800 text-[11px] mt-1">
                                      {loc.breakfastHours}
                                    </p>
                                  </div>
                                )}

                                {loc.lunchHours && (
                                  <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-2.5">
                                    <div className="flex items-center gap-1.5 font-bold text-emerald-900">
                                      <Clock className="w-3.5 h-3.5 text-emerald-700" />
                                      <span>Comidas</span>
                                    </div>
                                    <p className="text-emerald-800 text-[11px] mt-1">
                                      {loc.lunchHours}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Action Buttons */}
                              <div className="pt-1 flex flex-wrap items-center gap-2">
                                {loc.mapUrl && (
                                  <a
                                    href={loc.mapUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#001557] hover:bg-[#11255E] text-white text-xs font-bold shadow-xs transition-colors"
                                  >
                                    <Navigation className="w-3.5 h-3.5" />
                                    <span>Cómo llegar</span>
                                    <ExternalLink className="w-3 h-3 text-sky-300" />
                                  </a>
                                )}

                                {loc.deliveryUrl && (
                                  <a
                                    href={loc.deliveryUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#FF441F]/10 hover:bg-[#FF441F]/20 text-[#FF441F] text-xs font-bold border border-[#FF441F]/30 transition-colors"
                                  >
                                    <ShoppingBag className="w-3.5 h-3.5" />
                                    <span>Pedir en Rappi</span>
                                  </a>
                                )}
                              </div>
                            </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center text-gray-500">
                  <AlertCircle className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm font-bold text-gray-700">No se encontraron sucursales</p>
                  <button
                    onClick={() => { setSearchQuery(''); setSelectedZone('all'); }}
                    className="mt-2 text-xs font-bold text-[#001557] hover:underline"
                  >
                    Restablecer filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Interactive CDMX Visual Map (lg:col-span-6) */}
        <div className="lg:col-span-6">
          <div className="relative h-[560px] sm:h-[640px] rounded-2xl overflow-hidden border border-[#E2DDD5] bg-[#F7F5EE] shadow-lg flex flex-col justify-between group">
            
            {/* Background Map Visual */}
            <div className="absolute inset-0 z-0">
              <img
                src="/website/cdmx-map-visual-light.jpg"
                alt="Mapa Cartográfico Doña Blanca CDMX"
                className="w-full h-full object-cover object-center filter brightness-[0.92] contrast-[1.08]"
              />
              {/* Subtle tech vignette gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-white/40 pointer-events-none" />
            </div>

            {/* Map Top Header Badge Overlay */}
            <div className="relative z-20 p-4 sm:p-5 flex items-center justify-between gap-3 pointer-events-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 text-[#001557] border border-[#E2DDD5] backdrop-blur-md shadow-sm">
                <Compass className="w-4 h-4 text-sky-400" />
                <span className="text-xs font-bold tracking-wide">
                  Red CDMX · 13 Puntos Doña Blanca
                </span>
              </div>

              <a
                href="https://www.google.com/maps/search/?api=1&query=Dona+Blanca+Restaurante+CDMX"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 hover:bg-white text-[#001557] text-xs font-bold border border-white/50 backdrop-blur-md shadow-md transition-all hover:scale-105"
              >
                <span>Google Maps</span>
                <ExternalLink className="w-3 h-3 text-sky-600" />
              </a>
            </div>

            {/* Interactive Pins Coordinate Overlay */}
            <div className="absolute inset-0 z-10 pointer-events-auto">
              {publicLocations.map((loc) => {
                const coord = coordinatesBySlug[loc.slug];
                if (!coord) return null;

                const isActive = activeLocationId === loc.id;
                const isHovered = hoveredLocationId === loc.id;
                const isHighlight = isActive || isHovered;
                
                return (
                  <div
                    key={loc.id}
                    onClick={() => handleSelectLocation(loc.id, true)}
                    onMouseEnter={() => setHoveredLocationId(loc.id)}
                    onMouseLeave={() => setHoveredLocationId(null)}
                    style={{ left: `${coord.x}%`, top: `${coord.y}%` }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer z-20 group/pin select-none"
                  >
                    {/* Pulsing Radar Ring for Active Pin */}
                    {isHighlight && (
                      <span className="absolute -inset-3 rounded-full bg-sky-400/40 animate-ping" />
                    )}

                    {/* Outer Glow Ring */}
                    <div className={`relative rounded-full flex items-center justify-center transition-all duration-300 ${
                      isActive
                        ? 'w-7 h-7 bg-[#97472E] text-white shadow-[0_0_15px_rgba(151,71,46,0.6)] ring-2 ring-white scale-125 z-30'
                        : isHovered
                        ? 'w-6 h-6 bg-[#0284C7] text-white shadow-[0_0_12px_rgba(2,132,199,0.5)] scale-110 z-20'
                        : 'w-4.5 h-4.5 bg-[#001557] text-white ring-2 ring-white shadow-md hover:scale-125'
                    }`}>
                      <MapPin className={`${isActive ? 'w-4 h-4' : 'w-2.5 h-2.5'} ${isActive || isHovered ? 'opacity-100' : 'opacity-0'}`} />
                    </div>

                    {/* Floating Label for Active Pin */}
                    {isHighlight && (
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 pointer-events-none z-40 whitespace-nowrap">
                        <div className="px-2.5 py-1 rounded-md bg-white/95 text-[#001557] text-[11px] font-bold border border-[#E2DDD5] shadow-xl backdrop-blur-md flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                          <span>{loc.name}</span>
                          <span className="text-gray-400 font-normal">({loc.neighborhood || 'CDMX'})</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom Overlay: Active Location Quick Summary Card */}
            <div className="relative z-20 p-4 sm:p-5 pointer-events-auto">
              <div className="p-4 rounded-xl bg-white/95 border border-[#E2DDD5] text-gray-900 backdrop-blur-md shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-sky-400">
                      Sucursal Seleccionada en Mapa
                    </span>
                  </div>
                  <h4 className="font-heading font-black text-lg text-[#001557] tracking-tight truncate">
                    Doña Blanca {activeLoc.name}
                  </h4>
                  <p className="text-xs text-gray-600 truncate">
                    {activeLoc.addressLine1 ? `${activeLoc.addressLine1}, Col. ${activeLoc.neighborhood || ''}` : 'Ubicación en proceso de confirmación oficial'}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                  {activeLoc.mapUrl && (
                    <a
                      href={activeLoc.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#001557] hover:bg-[#11255E] text-white font-bold text-xs transition-colors shadow-md"
                    >
                      <Navigation className="w-3.5 h-3.5" />
                      <span>Ver Ruta</span>
                    </a>
                  )}
                  {activeLoc.deliveryUrl && (
                    <a
                      href={activeLoc.deliveryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs transition-colors"
                    >
                      <ShoppingBag className="w-3.5 h-3.5" />
                      <span>Rappi</span>
                    </a>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};
